/* eslint-disable react-hooks/exhaustive-deps */
import {
    CloseOutlined,
    EditOutlined,
    QuestionCircleOutlined,
    ShoppingOutlined,
} from '@ant-design/icons';
import {
    Button,
    Card,
    Col,
    DatePicker,
    Divider,
    Empty,
    Form,
    Image,
    Input,
    Popconfirm,
    Row,
    Select,
    Space,
    Switch,
    Table,
    Tooltip,
    Typography,
} from 'antd';
import { useWatch } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Eye, ShoppingCart } from 'lucide-react';
import { Activity, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as Check } from '../../../assets/svg/check.svg';
import { ReactComponent as Close } from '../../../assets/svg/close.svg';
import BalanceTag from '../../../components/BalanceTag';
import CustomerSearch from '../../../components/ui/CustomerSearch';
import QuantityInput from '../../../components/ui/QuantityInput/index';
import { getCustomerDetail } from '../../../redux/actions/customers';
import {
    addOrder,
    addOrderItem,
    getOpenOrderByCustomer,
    getOrdersById,
    postOrderToNetSuite,
    removeOrderItem,
    updateOrder,
} from '../../../redux/actions/order';
import { setSettings } from '../../../redux/slices/authSlice';
import { clearFilters, setCustomer } from '../../../redux/slices/filtersSlice';
import { setOrderItems, updateOrderQuantityById } from '../../../redux/slices/orderSlice';
import { store } from '../../../redux/store';
import { getRoutes } from '../../../routes/Routes';
import {
    calculateOrderSummary,
    calculateVAT,
    formatAddress,
    formatCurrency,
    getProductImageUrl,
    getTimeSlots,
} from '../../../utils';
import { errorToast, showToast } from '../../../utils/toast';
import AddProducts from '../../Orders/Create/AddProducts';
import DeliveryAddress from './DeliveryAddress';
import { updateOpenOrderQuantityById } from '../../../redux/slices/productsSlice';
import { setCustomerBalance } from '../../../redux/slices/customersSlice';
import { PLACEHOLDER_IMAGE } from '../../../constants';
import { getProductsWithPagination } from '../../../redux/actions/products';
import _ from 'lodash';
import PromotionalTabs from './../../../components/ui/PromotionalTabs/index';
import SuggestedProducts from './../../../components/ui/SuggestedProducts/index';

const { Text, Title } = Typography;

const CreateOrder = () => {
    const { orderId, invoiceId } = useParams();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const tomorrow = dayjs().add(1, 'day').startOf('day');

    const { settings } = useSelector((state) => state.authSlice);
    const { details, balance } = useSelector((state) => state.customersSlice);
    const { orderItems } = useSelector((state) => state.orderSlice);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [order, setOrder] = useState(null);
    const [editQuantity, setEditQuantity] = useState(null);
    const [promotions, setPromotions] = useState([]);

    const customerId = useWatch(['customer_netsuite_id'], form);
    const orderType = useWatch(['orderType'], form);
    const timeSlot = useWatch(['timeSlot'], form);
    const orderDate = useWatch(['order_date'], form);
    const timeSlots = getTimeSlots('09:00', '19:00', 15);

    const formatTimeSlot = () => {
        const endOfDay = dayjs().hour(19).minute(0);

        if (orderType === 1 && orderDate && dayjs(orderDate).isSame(dayjs(), 'day')) {
            let threeHoursLater = dayjs().add(3, 'hour');

            if (threeHoursLater.isAfter(endOfDay)) threeHoursLater = endOfDay;

            const minTime = dayjs().isAfter(threeHoursLater) ? dayjs() : threeHoursLater;

            return getTimeSlots(minTime.format('HH:mm'), '19:00', 15).filter(
                (slot) => dayjs(slot, 'HH:mm').isAfter(dayjs())
            );
        }
        return timeSlots;
    };

    const getSlotRange = (slot) => {
        const start = dayjs(slot, 'HH:mm');
        const end = start.add(10, 'minute');
        return `${start.format('HH:mm')} to ${end.format('HH:mm')}`;
    };

    const timeWindow = timeSlot ? getSlotRange(timeSlot) : null;

    const { subtotal, vat, grandTotal } = calculateOrderSummary(orderItems);

    const disabledDate = (current) => {
        const now = dayjs();
        const endOfToday = now.hour(19).minute(0);

        if (now.isAfter(endOfToday)) {
            return current && current.isBefore(now.startOf('day').add(1, 'day'), 'day');
        }

        return current && current < now.startOf('day');
    };

    const handleOrderTypeChange = (value) => {
        if (value === 2) {
            setIsModalOpen(true);
            form.setFieldsValue({ order_date: tomorrow });
        }
    };

    const handleQuantityChange = (value) => {
        setEditQuantity({ ...editQuantity, newQty: value });
        store.dispatch(updateOrderQuantityById({ id: editQuantity?.recordId, quantity: value }));
        store.dispatch(updateOpenOrderQuantityById({ id: editQuantity?.recordId, quantity: value }));
    };

    const handleQuantityUpdate = async () => {
        const body = {
            id: editQuantity?.recordId,
            order_id: orderId,
            quantity: editQuantity?.newQty,
        };

        if (editQuantity?.newQty) {
            const res = await addOrderItem(body, null, false);
            if (res?.ack) {
                setEditQuantity(null);
                getOrdersById(orderId);
            }
        }
    };

    const columns = [
        {
            dataIndex: 'image',
            key: 'thumbnail',
            align: 'center',
            width: 60,
            hidden: !settings?.showThumbnail,
            render: (_, record) => {
                const productImg = getProductImageUrl(record);
                const items = orderItems?.map((item) => getProductImageUrl(item) || PLACEHOLDER_IMAGE);

                return (
                    <Image.PreviewGroup items={items}>
                        <Image
                            src={productImg}
                            alt={record?.product_name}
                            width={50}
                            height={50}
                            fallback={PLACEHOLDER_IMAGE}
                            preview={{
                                mask: <Eye size={16} />,
                            }}
                        />
                    </Image.PreviewGroup>
                );
            },
        },
        { title: 'Code', dataIndex: 'product_code', key: 'product_code' },
        { title: 'Product', dataIndex: 'product_name', key: 'product_name' },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'center',
            width: 200,
            render: (text, record) => {
                const isEditing = editQuantity?.show && editQuantity?.recordId === record.id;
                return isEditing ? (
                    <Space size="small">
                        <QuantityInput
                            value={text}
                            onChange={(value) => handleQuantityChange(value)}
                            size="small"
                            min={1}
                        />
                        <Button
                            color="green"
                            variant="solid"
                            icon={<Check width={16} height={16} />}
                            size="small"
                            shape="round"
                            onClick={handleQuantityUpdate}
                        />
                        <Button
                            color="danger"
                            variant="solid"
                            icon={<Close width={16} height={16} />}
                            size="small"
                            shape="round"
                            onClick={() => {
                                store.dispatch(
                                    updateOrderQuantityById({ id: record.id, quantity: editQuantity?.quantity })
                                );
                                store.dispatch(
                                    updateOpenOrderQuantityById({ id: record.id, quantity: editQuantity?.quantity })
                                );
                                setEditQuantity(null);
                            }}
                        />
                    </Space>
                ) : (
                    <div className="flex align-items-center justify-content-center gap-2">
                        <span>{text}</span>
                        {!order?.posted_by && (
                            <EditOutlined
                                className="edit-icon text-md "
                                onClick={() => setEditQuantity({ show: true, recordId: record.id, quantity: text })}
                            />
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Price',
            dataIndex: 'unit_price',
            key: 'unit_price',
            align: 'center',
            render: (text) => formatCurrency(text),
        },
        {
            title: 'VAT (%)',
            dataIndex: 'vat',
            key: 'vat',
            align: 'center',
        },
        {
            title: 'VAT (£)',
            key: 'vat',
            align: 'center',
            render: (_, record) => {
                const { vatAmount } = calculateVAT({
                    unitPrice: record.unit_price,
                    quantity: record.quantity,
                    vatField: record.vat,
                });
                return formatCurrency(vatAmount);
            },
        },
        {
            title: 'Total',
            key: 'totalWithVat',
            align: 'center',
            render: (_, record) => {
                const { totalWithVAT } = calculateVAT({
                    unitPrice: record.unit_price,
                    quantity: record.quantity,
                    vatField: record.vat,
                });
                return formatCurrency(totalWithVAT);
            },
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            align: 'right',
            hidden: !!order?.posted_by,
            render: (_, record) => (
                <Popconfirm
                    title="Remove Item"
                    description="Are you sure you want to remove this item from the order?"
                    okText="Remove"
                    cancelText="Cancel"
                    okButtonProps={{
                        variant: 'solid',
                        color: 'danger',
                        size: 'medium',
                    }}
                    cancelButtonProps={{
                        variant: 'outlined',
                        color: 'danger',
                        size: 'medium',
                    }}
                    onConfirm={() => removeOrderItem(record?.id, orderId)}
                    classNames={{
                        body: 'max-w-20rem',
                    }}
                >
                    <Button
                        color="danger"
                        variant="solid"
                        shape="round"
                        size="small"
                        icon={
                            <CloseOutlined className="flex align-items-center justify-content-center text-xs" />
                        }
                    />
                </Popconfirm>
            ),
        },
    ];

    const getOrderDetails = async () => {
        const res = await getOrdersById(orderId || invoiceId);
        if (res?.ack) {
            const { fullAddress } = formatAddress(res?.order);
            setOrder(res?.order);
            setSelectedAddress(res?.order);
            form.setFieldsValue({
                ...res?.order,
                order_date: res?.order?.order_date ? dayjs(res?.order?.order_date) : null,
                orderType: res?.order?.type || 1,
                deliveryAddress: fullAddress,
                timeSlot: res?.order?.collection_slot || null,
            });
            if (res?.order?.posted_by) {
                navigate(getRoutes().invoice_details.route(res?.order?.id));
            }
            if (res?.order?.customer_netsuite_id) getCustomerDetailById(res?.order?.customer_netsuite_id);
            !res?.order.posted_by && getPromotions(res?.order?.customer_netsuite_id);
        } else if (orderId) {
            navigate(getRoutes().order.route());
        } else if (invoiceId) {
            navigate(getRoutes().invoice.route());
        }
    };

    const getCustomerDetailById = async (customer) => {
        store.dispatch(setCustomer(customer));
        form.setFieldsValue({ customer_netsuite_id: customer });

        if (!customer) return;

        // Check Open Order on Create
        if (!orderId && !invoiceId) {
            const openOrder = await getOpenOrderByCustomer(customer);
            if (openOrder?.ack && openOrder?.order_id) {
                showToast('An active order already exists. Redirecting to details');
                return navigate(getRoutes().order_details.route(openOrder?.order_id));
            }
        }

        // Get Customer Details
        const customerDetail = await getCustomerDetail(customer);
        if (customerDetail?.ack) {
            getPromotions(customer);

            // If customer have single address
            if (customerDetail?.customer_details?.length === 1) {
                const address = customerDetail?.customer_details?.[0];
                setSelectedAddress(address);
                const { fullAddress } = formatAddress(address);
                form.setFieldsValue({ deliveryAddress: fullAddress });
            }
        }
    };

    const handleAddressSelection = (address) => {
        setSelectedAddress(address);
        const { fullAddress } = formatAddress(address);
        form.setFieldsValue({ deliveryAddress: fullAddress });
    };

    const handleFinish = async ({ type }) => {
        const values = await form.getFieldsValue(true);
        const body = {
            order_date: values.order_date.format('YYYY-MM-DD'),
            customer_netsuite_id: values?.customer_netsuite_id,
            type: values?.orderType,
            collection_slot: values?.timeSlot || null,
            vehicle_reg_no: values?.vehicle_reg_no || null,
            address_internal_id: selectedAddress?.address_internal_id || null,
            addressee: selectedAddress?.addressee || null,
            address_1: selectedAddress?.address_1 || null,
            address_2: selectedAddress?.address_2 || null,
            city: selectedAddress?.city || null,
            postcode: selectedAddress?.postcode || null,
            notes: values?.notes?.trim() || null,
        };
        if (orderId) body.id = orderId;
        const res = orderId ? await updateOrder(body) : await addOrder(body);
        if (res?.ack && type === 'ADD_PRODUCTS') {
            navigate(getRoutes().order_details.route(res?.record_id));
            setDrawerOpen(true);
        }
    };

    const handleAddProduct = async () => {
        if (!customerId) {
            errorToast('Please select a customer to create an order.');
        } else if (!orderId) {
            handleFinish({ type: 'ADD_PRODUCTS' });
        } else {
            setDrawerOpen(true);
        }
    };

    const handlePostOrder = async () => {
        try {
            await form.validateFields();

            if (!selectedAddress) {
                errorToast('Please select a delivery address for delivery orders.');
                return;
            }

            form.submit();

            const res = await postOrderToNetSuite({ id: orderId });
            if (res?.ack) {
                navigate(getRoutes().order.route());
            }
        } catch (error) {
            if (error?.errorFields?.length) {
                form.setFields(error.errorFields);
                form.scrollToField(error.errorFields[0].name);
            }
        }
    };

    const getPromotions = async (customerId) => {
        const res = await getProductsWithPagination({
            body: {
                customer_id: customerId,
                offer: 1,
            },
            isAppend: false,
            isDispatch: false,
        });
        if (res?.ack) {
            setPromotions(res?.products);
        }
        return res;
    };

    const filteredPromoProducts = _.filter(
        promotions,
        (promo) => !_.some(orderItems, { product_netsuite_id: promo.netsuite_id })
    );

    useEffect(() => {
        form.setFieldsValue({ order_date: tomorrow });
    }, [form]);

    useEffect(() => {
        store.dispatch(clearFilters())
        store.dispatch(setOrderItems([]));
        store.dispatch(setCustomerBalance(null));

        if (orderId || invoiceId) {
            getOrderDetails();
        }
    }, [orderId, invoiceId]);

    return (
        <div className="create-order-page">
            <div className="flex align-items-center justify-content-between mb-1 flex-wrap gap-1">
                <div>
                    <Title level={3} className="page-title m-0">
                        {invoiceId ? 'Invoice' : orderId ? 'Update Order' : 'Create Order'}
                    </Title>
                </div>
                <div className="flex align-items-center gap-1">
                    <Activity mode={invoiceId ? 'hidden' : 'visible'}>
                        <BalanceTag balance={balance} />
                    </Activity>
                    {orderId && !!orderItems?.length && !order?.posted_by && (
                        <Popconfirm
                            title="Confirm Submission"
                            description="Once you submit this order, you won’t be able to make any changes."
                            okText="Submit"
                            cancelText="Cancel"
                            okButtonProps={{
                                variant: 'solid',
                                color: 'danger',
                                size: 'medium',
                            }}
                            cancelButtonProps={{
                                variant: 'outlined',
                                color: 'danger',
                                size: 'medium',
                            }}
                            onConfirm={handlePostOrder}
                            classNames={{
                                body: 'max-w-20rem',
                            }}
                        >
                            <Button size="large" type="primary" className="submit-order-btn">
                                Submit Order
                            </Button>
                        </Popconfirm>
                    )}
                    {(order?.order_code || order?.invoice_code) && (
                        <div className="flex align-items-center gap-2">
                            {order?.order_code && (
                                <Title level={4} className="m-0">
                                    Order# {order.order_code}
                                </Title>
                            )}

                            {order?.order_code && order?.invoice_code && <Divider type="vertical" style={{ background: 'gray' }} />}

                            {order?.invoice_code && (
                                <Title level={4} className="m-0">
                                    Invoice# {order.invoice_code}
                                </Title>
                            )}
                        </div>
                    )}

                </div>
            </div>
            <Row gutter={[8, 8]} align="stretch">
                <Col xs={24} lg={24} xl={18}>
                    <Form
                        form={form}
                        layout="vertical"
                        name="orderInfo"
                        className="h-full"
                        initialValues={{ orderType: 1, code: 'To be generated' }}
                        onFinish={(values) => handleFinish({ values })}
                        onValuesChange={(changedValues, allValues) => {
                            const changedField = Object.keys(changedValues)[0];
                            if (['customer_netsuite_id'].includes(changedField)) {
                                const { customer_netsuite_id } = allValues;
                                if (customer_netsuite_id) {
                                    getPromotions(customer_netsuite_id);
                                }
                            }
                        }}
                        disabled={!!order?.posted_by}
                    >
                        <Space
                            direction="vertical"
                            size="small"
                            className="main-container w-full h-full flex flex-column"
                        >
                            {/* Primary Information */}
                            <Card
                                title="Primary Information"
                                classNames={{
                                    body: 'p-3',
                                }}
                            >
                                <Row gutter={8}>
                                    <Col sm={24} lg={12} className="w-full">
                                        <Form.Item
                                            label="Customer"
                                            name="customer_netsuite_id"
                                            rules={[{ required: true, message: 'Please select a customer' }]}
                                        >
                                            <CustomerSearch
                                                size="large"
                                                placeholder="Search and select customer..."
                                                showUnassigned={false}
                                                onChange={getCustomerDetailById}
                                                value={customerId}
                                                disabled={!!orderItems?.length}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col sm={24} lg={6} xl={customerId ? 4 : 6} className="w-full">
                                        <Form.Item label="Order Code" name="code">
                                            <Input disabled placeholder="Order Code" size="large" />
                                        </Form.Item>
                                    </Col>
                                    <Col sm={24} lg={6} xl={customerId ? 4 : 6} className="flex-1">
                                        <Form.Item
                                            name="order_date"
                                            label={`Date to ${orderType === 1 ? 'Collect' : 'Deliver'}`}
                                            className="w-full"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'date is required',
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                size="large"
                                                style={{ width: '100%' }}
                                                format="DD-MM-YYYY"
                                                disabledDate={disabledDate}
                                                showNow={false}
                                                onChange={() => form.resetFields(['timeSlot'])}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Activity mode={customerId ? 'visible' : 'hidden'}>
                                        <Col sm={24} lg={4} className="w-full">
                                            <Form.Item
                                                label="Order Type"
                                                name="orderType"
                                                rules={[{ required: true, message: 'Please select order type' }]}
                                            >
                                                <Select
                                                    size="large"
                                                    placeholder="Select order type"
                                                    onChange={handleOrderTypeChange}
                                                    options={[
                                                        { label: 'Collection', value: 1 },
                                                        { label: 'Delivery', value: 2 },
                                                    ]}
                                                    optionRender={(option) => (
                                                        <div
                                                            role="button"
                                                            onClick={() => {
                                                                if (option.data.value === 2) setIsModalOpen(true);
                                                            }}
                                                        >
                                                            <span aria-label={option.data.label}>{option.data.icon}</span>

                                                            {option.data.label}
                                                        </div>
                                                    )}
                                                />
                                            </Form.Item>
                                        </Col>

                                        <Activity mode={orderType === 1 ? 'visible' : 'hidden'}>
                                            <Col sm={24} lg={4} className="w-full">
                                                <Form.Item
                                                    label={
                                                        <div>
                                                            Time Slot
                                                            <Activity mode={timeSlot ? 'visible' : 'hidden'}>
                                                                <Tooltip title={`Your time slot for collection is ${timeWindow}.`}>
                                                                    <QuestionCircleOutlined className="text-lg ml-1 cursor-pointer" />
                                                                </Tooltip>
                                                            </Activity>
                                                        </div>
                                                    }
                                                    name="timeSlot"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Time Slot is required',
                                                        },
                                                    ]}
                                                >
                                                    <Select
                                                        size="large"
                                                        placeholder="Select time slot"
                                                        options={formatTimeSlot()?.map((slot) => ({
                                                            label: slot,
                                                            value: slot,
                                                        }))}
                                                        className="w-full"
                                                    />
                                                </Form.Item>
                                            </Col>

                                            <Col sm={24} lg={4} className="w-full">
                                                <Form.Item name="vehicle_reg_no" label="Reg. No">
                                                    <Input placeholder="Vehicle Registration No." size="large" />
                                                </Form.Item>
                                            </Col>
                                        </Activity>

                                        <Activity mode={orderType === 2 ? 'visible' : 'hidden'}>
                                            <Col sm={24} lg={10} xl={8} className="w-full">
                                                <Form.Item
                                                    label="Delivery Address"
                                                    name="deliveryAddress"
                                                    className="w-full"
                                                >
                                                    <Input
                                                        readOnly
                                                        placeholder="Delivery address"
                                                        size="large"
                                                        suffix={
                                                            details?.length > 1 ? (
                                                                <EditOutlined onClick={() => setIsModalOpen(true)} />
                                                            ) : null
                                                        }
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Activity>
                                    </Activity>

                                    <Col sm={24} lg={10} xl={customerId ? 16 : 24} className="w-full">
                                        <Form.Item name="notes" label="Notes">
                                            <TextArea
                                                size="large"
                                                autoSize={{ minRows: 1, maxRows: 4 }}
                                                placeholder="Add any special instructions or notes..."
                                                style={{ borderRadius: '8px' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Products Card */}
                            <Card
                                title="Items"
                                className="products-card flex flex-column"
                                classNames={{
                                    body: 'p-3 flex-1',
                                }}
                                extra={
                                    <Space size="small">
                                        <Space align="center" size={4}>
                                            <Typography.Text className="form-item-label">Show Thumbnails</Typography.Text>
                                            <Switch
                                                checked={settings?.showThumbnail}
                                                onChange={() =>
                                                    store.dispatch(
                                                        setSettings({
                                                            showThumbnail: !settings?.showThumbnail,
                                                        })
                                                    )
                                                }
                                                disabled={!orderItems?.length}
                                            />
                                        </Space>

                                        <Button
                                            type="primary"
                                            icon={<ShoppingCart size={16} />}
                                            className="add-product-button"
                                            onClick={handleAddProduct}
                                        >
                                            <span className="btn-txt">Add Products</span>
                                        </Button>
                                    </Space>
                                }
                            >
                                <Table
                                    className="order-items-table"
                                    size="small"
                                    bordered={false}
                                    columns={columns}
                                    dataSource={orderItems}
                                    rowKey={(record) => record.id || record.netsuite_id}
                                    pagination={false}
                                    locale={{
                                        emptyText: (
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description={
                                                    <Text type="secondary">
                                                        No products added yet. Click "Add Product" to start
                                                    </Text>
                                                }
                                            />
                                        ),
                                    }}
                                    scroll={{ x: 'max-content', y: '500px' }}
                                    components={{
                                        body: {
                                            cell: (props) => (
                                                <td
                                                    {...props}
                                                    className={clsx({
                                                        'py-0': !!orderItems?.length && settings?.showThumbnail,
                                                    })}
                                                />
                                            ),
                                        },
                                    }}
                                />
                            </Card>
                        </Space>
                    </Form>
                </Col>

                {/* MIDDLE COLUMN */}
                <Col xs={24} lg={24} xl={6}>
                    <Space direction="vertical" size="small" className="w-full h-full flex flex-column">
                        <Card
                            className="order-summary-card"
                            title="Order Summary"
                            classNames={{
                                body: 'p-3',
                            }}
                        >
                            <div className="summary-item">
                                <Text className="text-md">TOTAL AMOUNT (EXC. VAT):</Text>
                                <Text strong className="text-lg">
                                    {formatCurrency(subtotal)}
                                </Text>
                            </div>

                            <div className="summary-item">
                                <Text className="text-md">VAT:</Text>
                                <Text strong className="text-lg">
                                    {formatCurrency(vat)}
                                </Text>
                            </div>

                            <Divider />

                            <div className="summary-item grand-total">
                                <Text strong>TOTAL AMOUNT (INC. VAT):</Text>
                                <Text strong>{formatCurrency(grandTotal)}</Text>
                            </div>

                            <Divider />

                            <Space direction="vertical" className="action-buttons mt-4">
                                {!order?.posted_by && (
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<ShoppingOutlined />}
                                        block
                                        className="create-order-btn"
                                        onClick={() => form.submit()}
                                    >
                                        {orderId ? 'Update Order' : 'Save Order'}
                                    </Button>
                                )}
                                <Button
                                    size="large"
                                    block
                                    onClick={() =>
                                        invoiceId
                                            ? navigate(getRoutes().invoice.route())
                                            : navigate(getRoutes().order.route())
                                    }
                                    danger
                                    ghost
                                >
                                    Cancel
                                </Button>
                            </Space>
                        </Card>

                        {/* Promotional Tabs */}
                        <PromotionalTabs products={filteredPromoProducts} />
                    </Space>
                </Col>

                {/* Suggested Products */}
                <Col span={24}>
                    <SuggestedProducts products={filteredPromoProducts} orderId={orderId} />
                </Col>
            </Row>

            {details && details.length > 1 && isModalOpen && (
                <DeliveryAddress
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    details={details}
                    onSelect={handleAddressSelection}
                    selectedAddress={selectedAddress}
                />
            )}

            {isDrawerOpen && (
                <AddProducts open={isDrawerOpen} onClose={() => setDrawerOpen(false)} isDrawer />
            )}
        </div>
    );
};

export default CreateOrder;
